'use client'
import { Link } from "@/locales/routing"
import { Markdown } from "@/components/Markdown"
import { useResponsiveSize } from "@/provider/useResponsiveSize"
import { resizeImage } from "@/util/image"
import { toText } from "@/util/toText"
import { cn, Image, Skeleton } from "@heroui/react"
import { useTranslations } from "next-intl"
import numeral from "numeral"
import { memo, useEffect, useMemo, useRef, useState } from "react"

/** 与 DiscoverCard 一致的占位骨架（竖向长方形，5/7 比例） */
export const DiscoverSkeletonCard = ({ className }: { className?: string }) => (
  <Skeleton className={cn("aspect-[5/7] w-full rounded-[14px]", className)}>
    <div className="h-full w-full" />
  </Skeleton>
)

/**
 * 阿里云 CDN：强制按宽度缩放。
 * 与 resizeImage 不同，这里即使链接已带 image_process 也会把宽度替换为目标值，
 * 用于把列表里偏小的头像(w_200)放大到能清晰填充长方形卡片。
 */
const fillImage = (url?: string | null, width = 420): string => {
  if (!url) return ""
  if (url.includes("image_process=")) return url.replace(/w_\d+/, `w_${width}`)
  const sep = url.includes("?") ? "&" : "?"
  return `${url}${sep}image_process=resize,w_${width}`
}

/** 打字机效果：active 为 true 时逐字输出 text */
const useTypewriter = (text: string, active: boolean, speed = 22) => {
  const [out, setOut] = useState("")
  const timer = useRef<ReturnType<typeof setInterval>>(null!)

  useEffect(() => {
    clearInterval(timer.current)
    if (!active || !text) {
      setOut("")
      return
    }
    let i = 0
    setOut("")
    timer.current = setInterval(() => {
      i += 1
      setOut(text.slice(0, i))
      if (i >= text.length) clearInterval(timer.current)
    }, speed)
    return () => clearInterval(timer.current)
  }, [text, active, speed])

  return out
}

const ChatBubbleIcon = (props: any) => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" {...props}>
    <path
      d="M8 1.5c3.728 0 6.75 2.518 6.75 5.625 0 3.107-3.022 5.625-6.75 5.625-.69 0-1.356-.086-1.983-.246l-2.69 1.214a.5.5 0 0 1-.703-.51l.23-2.36C1.74 10.36 1.25 9.06 1.25 7.125 1.25 4.018 4.272 1.5 8 1.5Z"
      fill="currentColor"
    />
  </svg>
)

const HeartIcon = (props: any) => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" {...props}>
    <path
      d="M8 13.6 2.9 8.7a3.2 3.2 0 0 1 0-4.6 3.3 3.3 0 0 1 4.6 0l.5.5.5-.5a3.3 3.3 0 0 1 4.6 0 3.2 3.2 0 0 1 0 4.6L8 13.6Z"
      fill="currentColor"
    />
  </svg>
)

type DiscoverCardProps = {
  robotId: number | string
  avatar?: string
  backgroundImage?: string
  isBackgroundImage?: number | boolean
  name?: any
  desc?: any
  greeting?: any
  comments?: number
  likes?: number
  author?: any
  authorAvatar?: string
  /** 逗号分隔的分类字符串 */
  categories?: string
  /** 点击「开始聊天」按钮（调用方负责登录校验 + 发起聊天） */
  onChat?: () => void
  className?: string
  /** 移动端是否展开问候语（受控；用于同一列表内仅展开一个，点开新卡片自动收起其他） */
  mobileActive?: boolean
  /** 移动端首次点击时回调，参数为当前 robotId（父级用于记录当前展开项） */
  onMobileActivate?: (id: number | string) => void
}

export const DiscoverCard = memo(({
  robotId,
  avatar,
  backgroundImage,
  isBackgroundImage,
  name,
  desc,
  greeting,
  comments,
  likes,
  author,
  authorAvatar,
  categories,
  onChat,
  className,
  mobileActive,
  onMobileActivate,
}: DiscoverCardProps) => {
  const t = useTranslations()
  // 桌面端鼠标悬浮展开问候语；移动端用点击展开（受控优先，未受控时退回自身状态）
  const isMobile = useResponsiveSize(['default', 'lg']) !== 'lg'
  const [hover, setHover] = useState(false)
  const [mobileOpenSelf, setMobileOpenSelf] = useState(false)
  const controlled = onMobileActivate !== undefined
  const mobileOpen = controlled ? !!mobileActive : mobileOpenSelf
  // 最终是否展开问候浮层：桌面看 hover，移动看 mobileOpen
  const expanded = isMobile ? mobileOpen : hover

  const nameText = toText(name)
  const descText = toText(desc)
  const authorText = toText(author)
  // 问候语过长时截断，避免打字过久（其余内容由 hover 区自动滚动展示）
  const greetingText = (toText(greeting) || descText).slice(0, 600)

  const tags = useMemo(
    () => (categories || "").split(",").map(s => s.trim()).filter(Boolean).slice(0, 3),
    [categories],
  )

  // 有背景图则用背景图填充，否则用头像填充（均裁剪填满长方形）
  const fillSrc = fillImage(isBackgroundImage && backgroundImage ? backgroundImage : avatar, 420)
  const typed = useTypewriter(greetingText, expanded)

  // 问候语自动滚动：打字阶段跟随到底部；打字完成后若内容溢出则缓慢上下循环滚动
  const scrollRef = useRef<HTMLDivElement>(null!)
  useEffect(() => {
    const el = scrollRef.current
    if (!el) return
    if (!expanded) {
      el.scrollTop = 0
      return
    }
    // 打字未结束：始终跟随最新内容
    if (typed.length < greetingText.length) {
      el.scrollTop = el.scrollHeight
      return
    }
    // 打字完成：内容未溢出则无需滚动
    const max = el.scrollHeight - el.clientHeight
    if (max <= 2) return
    let raf = 0
    let dir = 1
    let pause = 50
    // 用累加变量记录位置，避免部分浏览器把小数 scrollTop 取整后卡住不动
    let pos = 0
    el.scrollTop = 0
    const step = () => {
      if (pause > 0) {
        pause -= 1
      } else {
        pos += dir * 0.5
        if (pos >= max) {
          pos = max
          dir = -1
          pause = 60
        } else if (pos <= 0) {
          pos = 0
          dir = 1
          pause = 60
        }
        el.scrollTop = pos
      }
      raf = requestAnimationFrame(step)
    }
    raf = requestAnimationFrame(step)
    return () => cancelAnimationFrame(raf)
  }, [expanded, typed, greetingText])

  return (
    <Link
      href={`/robot/${robotId}`}
      // 列表几十张卡片同时自动预取 RSC，会在弱网下把请求队列塞满几十秒，
      // 反而拖慢用户真正点击后的导航（预取请求 in-flight 时导航会等它）。
      // 关闭自动预取：点击后立刻发高优先级导航请求，骨架屏在首字节到达时显示。
      prefetch={false}
      data-robot-id={robotId}
      onMouseEnter={() => { if (!isMobile) setHover(true) }}
      onMouseLeave={() => { if (!isMobile) setHover(false) }}
      onClick={(e) => {
        // 移动端：首次点击先展开问候语（可稳定查看），再次点击卡片才进入详情
        if (isMobile && !mobileOpen) {
          e.preventDefault()
          if (controlled) onMobileActivate!(robotId)
          else setMobileOpenSelf(true)
        }
      }}
      className={cn(
        "group relative block w-full select-none overflow-hidden rounded-[14px] bg-light-gray",
        "aspect-[5/7] shadow-sm transition-shadow duration-300 hover:shadow-xl",
        className,
      )}
    >
      {/* 填充图：背景图 / 头像。
          注意：不要给 HeroUI Image 传 loading="lazy" —— 它内部用脱离文档的
          预加载器判断加载完成才把 opacity 置 1，而脱离文档的 lazy 图片按规范
          不会加载，会导致图片永远不可见 */}
      <Image
        removeWrapper
        radius="none"
        alt={nameText}
        src={fillSrc}
        className={cn(
          // object-top：竖版立绘从顶部开始裁切，保证人物头部完整（居中裁切会切头）
          "absolute inset-0 z-0 h-full w-full object-cover object-top transition-transform duration-500 ease-out",
          expanded && "scale-[1.06]",
        )}
      />

      {/* 默认态：底部渐变 + 名称 / 分类 / 简介 / 作者 / 热度 */}
      <div className={cn(
        "absolute inset-x-0 bottom-0 z-10 bg-gradient-to-t from-black/95 via-black/60 to-transparent p-3 pt-14 transition-opacity duration-300",
        expanded && "opacity-0",
      )}>
        <p className="line-clamp-1 text-[15px] font-bold leading-tight text-white [text-shadow:0_1px_3px_rgb(0_0_0/55%)]">
          {nameText}
        </p>

        {!!tags.length && (
          <div className="mt-1.5 flex flex-wrap gap-1 overflow-hidden h-[18px]">
            {tags.map(tag => (
              <span key={tag} className="rounded-full bg-white/20 px-1.5 text-[10px] leading-[18px] text-white backdrop-blur-sm">
                {tag}
              </span>
            ))}
          </div>
        )}

        {descText && (
          <p className="mt-1.5 line-clamp-2 text-[12px] leading-snug text-white/85 [text-shadow:0_1px_2px_rgb(0_0_0/55%)]">
            {descText}
          </p>
        )}

        <div className="mt-2 flex items-center justify-between gap-2 text-[11px] text-white/80">
          <div className="flex items-center gap-2.5">
            <span className="flex items-center gap-1"><ChatBubbleIcon width={12} height={12} />{numeral(comments || 0).format("0a")}</span>
            {!!likes && <span className="flex items-center gap-1"><HeartIcon width={12} height={12} />{numeral(likes).format("0a")}</span>}
          </div>
          {!!authorText && (
            <div className="flex min-w-0 items-center gap-1">
              {authorAvatar && (
                <Image removeWrapper radius="full" alt={authorText} src={resizeImage(authorAvatar, 60)} className="h-4 w-4 flex-shrink-0 rounded-full object-cover" />
              )}
              <span className="truncate max-w-[80px]">{authorText}</span>
            </div>
          )}
        </div>
      </div>

      {/* hover 态：头像 + 作者 + 分类 + 问候语(markdown 打字机) + 开始聊天 */}
      <div
        className={cn(
          "absolute inset-0 z-20 flex flex-col bg-gradient-to-b from-black/85 via-black/80 to-black/90 p-3 backdrop-blur-sm",
          "transition-opacity duration-300",
          expanded ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none",
        )}
      >
        <div className="flex items-center gap-2">
          <Image
            removeWrapper
            radius="full"
            alt={nameText}
            src={resizeImage(avatar, 100)}
            className="h-9 w-9 flex-shrink-0 rounded-full object-cover ring-2 ring-white/30"
          />
          <div className="min-w-0">
            <p className="line-clamp-1 text-[14px] font-bold text-white">{nameText}</p>
            {!!authorText && (
              <p className="line-clamp-1 text-[11px] text-white/60">{t("common.by")}{authorText}</p>
            )}
          </div>
        </div>

        {!!tags.length && (
          <div className="mt-2 flex flex-wrap gap-1 overflow-hidden h-[18px]">
            {tags.map(tag => (
              <span key={tag} className="rounded-full bg-white/15 px-1.5 text-[10px] leading-[18px] text-white/90">
                {tag}
              </span>
            ))}
          </div>
        )}

        <div ref={scrollRef} className="relative mt-2 flex-1 overflow-hidden text-white/90 [text-shadow:0_1px_2px_rgb(0_0_0/45%)]">
          <Markdown stripImages className="text-[12.5px] !leading-[1.5] text-white/90 [&_p]:my-1 [&_a]:!text-white [&_strong]:text-white [&_em]:text-white/90 [&_blockquote]:!border-white/30 [&_blockquote]:!text-white/70">
            {typed}
          </Markdown>
        </div>

        <button
          type="button"
          onClick={(e) => {
            // 阻止冒泡到卡片链接，避免点击聊天按钮时跳转到详情页
            e.preventDefault()
            e.stopPropagation()
            onChat?.()
          }}
          className={cn(
            "mt-2 flex w-full items-center justify-center gap-1.5 rounded-full py-2",
            "bg-white text-[13px] font-semibold text-[#18181b] shadow-md",
            "transition-transform duration-150 hover:bg-white/90 active:scale-[0.97]",
          )}
        >
          <ChatBubbleIcon width={15} height={15} />
          {t("common.startChat")}
        </button>
      </div>
    </Link>
  )
})

DiscoverCard.displayName = "DiscoverCard"
